import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { apiService, ProjectStatus } from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { useSearch } from "../../contexts/SearchContext";
import { InputIcon } from "primereact/inputicon";
import { IconField } from "primereact/iconfield";

const ProjectStatusAdmin: React.FC = () => {
  const [projectStatuses, setProjectStatuses] = useState<ProjectStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const { searchTerm: filter, setSearchTerm: setFilter } = useSearch();
  const [modalVisible, setModalVisible] = useState(false);
  const [editProjectStatus, setEditProjectStatus] =
    useState<ProjectStatus | null>(null);
  const [formName, setFormName] = useState("");
  const [formCompleted, setFormCompleted] = useState(false);
  const [formWin, setFormWin] = useState(false);

  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const res = await apiService.getProjectStatuses();
    if (res.success) setProjectStatuses(res.data);
    else
      toast.showError(
        "Failed",
        res.message || "Unable to load project statuses"
      );
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      projectStatuses.filter((ps) =>
        ps.name.toLowerCase().includes(filter.toLowerCase())
      ),
    [projectStatuses, filter]
  );

  const openNew = () => {
    setEditProjectStatus(null);
    setFormName("");
    setFormCompleted(false);
    setFormWin(false);
    setModalVisible(true);
  };

  const openEdit = (projectStatus: ProjectStatus) => {
    setEditProjectStatus(projectStatus);
    setFormName(projectStatus.name);
    setFormCompleted(projectStatus.completed);
    setFormWin(projectStatus.win);
    setModalVisible(true);
  };

  const save = async () => {
    if (!formName.trim()) {
      toast.showWarn("Validation", "Name is required");
      return;
    }

    const payload = {
      id: editProjectStatus?.id,
      name: formName.trim(),
      completed: formCompleted,
      win: formWin,
    };

    const res = await apiService.createOrUpdateProjectStatus(payload);
    if (res.success) {
      toast.showSuccess("Saved", `Project status '${res.data.name}' saved`);
      setModalVisible(false);
      await load();
    } else {
      toast.showError("Error", res.message || "Save failed");
    }
  };

  const remove = async (projectStatus: ProjectStatus) => {
    const res = await apiService.deleteProjectStatus(projectStatus.id);
    if (res.success) {
      toast.showSuccess(
        "Deleted",
        `Project status '${projectStatus.name}' deleted`
      );
      await load();
    } else {
      toast.showError("Error", res.message || "Delete failed");
    }
  };

  const completedBodyTemplate = (rowData: ProjectStatus) => {
    return rowData.completed ? (
      <Tag severity="success" value="Completed" icon="pi pi-check" />
    ) : (
      <Tag severity="warning" value="In Progress" icon="pi pi-clock" />
    );
  };

  const winBodyTemplate = (rowData: ProjectStatus) => {
    if (!rowData.completed) {
      return <span className="text-500">-</span>;
    }
    return rowData.win ? (
      <Tag severity="success" value="Win" icon="pi pi-check-circle" />
    ) : (
      <Tag severity="danger" value="Loss" icon="pi pi-times-circle" />
    );
  };

  const header = (
    <div className="flex justify-content-between align-items-center">
      <div className="flex align-items-center gap-2">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search project statuses..."
          />
        </IconField>
      </div>
      <Button label="Add" icon="pi pi-plus" onClick={openNew} />
    </div>
  );

  return (
    <div>
      <DataTable
        value={filtered}
        loading={loading}
        header={header}
        emptyMessage="No project statuses found"
        className="p-datatable-sm"
        paginator
        rows={10}
      >
        <Column field="name" header="Name" sortable />
        <Column
          field="completed"
          header="Status"
          body={completedBodyTemplate}
          sortable
          style={{ width: "150px" }}
        />
        <Column
          field="win"
          header="Outcome"
          body={winBodyTemplate}
          sortable
          style={{ width: "120px" }}
        />
        <Column
          header="Actions"
          body={(row: ProjectStatus) => (
            <div className="flex gap-2 justify-content-end">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-outlined p-button-sm"
                onClick={() => openEdit(row)}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-outlined p-button-sm p-button-danger"
                onClick={() => remove(row)}
              />
            </div>
          )}
          style={{ width: "120px" }}
          headerStyle={{ textAlign: "right" }}
        />
      </DataTable>

      <Dialog
        header={
          editProjectStatus ? "Edit Project Status" : "Add Project Status"
        }
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: 420 }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="field">
            <label className="block mb-2">Name *</label>
            <InputText
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full"
              placeholder="Enter status name"
            />
          </div>

          <div className="field">
            <div className="flex align-items-center">
              <Checkbox
                id="completed"
                checked={formCompleted}
                onChange={(e) => setFormCompleted(e.checked || false)}
                className="mr-2"
              />
              <label htmlFor="completed" className="block">
                Completed
              </label>
            </div>
            <small className="text-500">
              Mark if this status indicates project completion
            </small>
          </div>

          <div className="field">
            <div className="flex align-items-center">
              <Checkbox
                id="win"
                checked={formWin}
                onChange={(e) => setFormWin(e.checked || false)}
                className="mr-2"
                disabled={!formCompleted}
              />
              <label
                htmlFor="win"
                className={`block ${!formCompleted ? "text-500" : ""}`}
              >
                Win
              </label>
            </div>
            <small className="text-500">
              {formCompleted
                ? "Mark if this completed status represents a win (vs. loss)"
                : "Only available for completed statuses"}
            </small>
          </div>

          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setModalVisible(false)}
            />
            <Button label="Save" icon="pi pi-check" onClick={save} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProjectStatusAdmin;
